import React, { useState, useCallback } from 'react';

// Main App component
const OpthalmologyyExamination = () => {
    // State to hold all form data
    const [formData, setFormData] = useState({
        // B. PRE-OPERATIVE EXAMINATION
        vaPresentingRight: '', vaPresentingLeft: '',
        vaBestRight: '', vaBestLeft: '',
        lensRight: '', lensLeft: '',
        pathology: '', pathologySpecify: '',
        
        // Optional B
        eyeToOperate: 'Right', // Default
        refractionSphere: '', refractionCylinder: '', refractionAxis: '',
        targetedSphericalEquivalent: '',
        biometryK1: '', biometryK2: '', axialLength: '',

        // C. SURGERY
        date: '',
        placeOfOperation: '1',
        hospitalId: '',
        surgeonId: '',
        training: '1',
        typeOfSurgery: '4',
        iol: '1',
        complication: [],
        otherComplicationSpecify: '',

        // Optional C
        incisionType: '',
        capsulotomyType: '',
        typeIOL: '',
        iolPower: '',
        sutureType: '',
        numberOfSutures: '',
    });

    // Universal change handler for all inputs and single-select radios
    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value,
        }));
    }, []);

    // Handler for Complication checkboxes (multi-select)
    const handleComplicationChange = useCallback((e) => {
        const { value, checked } = e.target;
        setFormData(prevData => {
            const currentComplications = prevData.complication;
            
            let newComplications;
            if (checked) {
                newComplications = [...currentComplications, value];
            } else {
                newComplications = currentComplications.filter(c => c !== value);
                if (value === '9') {
                    // Clear specify text if "Others" is unchecked
                    return { ...prevData, complication: newComplications, otherComplicationSpecify: '' };
                }
            }
            return { ...prevData, complication: newComplications };
        });
    }, []);

    // --- Data Definitions (for rendering) ---

    const lensExamOptions = [
        { label: "Clear lens:", value: 1 },
        { label: "Opacity, not ready for operation:", value: 2 },
        { label: "Operable cataract:", value: 3 },
        { label: "Inoperable cataract:", value: 4 },
        { label: "Aphakia:", value: 5 },
        { label: "Pseudophakia:", value: 6 },
        { label: "Cannot examine:", value: 7 },
    ];
    
    const pathologyOptions = [
        { label: "Corneal scar:", value: 1 },
        { label: "Old iritis:", value: 2 },
        { label: "Retinal disease (Diabetes, AMD, etc.):", value: 3 },
        { label: "Glaucoma:", value: 4 },
    ];

    const placeOptions = [
        { label: "Base hospital", value: '1' },
        { label: "Other hospital", value: '2' },
        { label: "Out of hospital", value: '3' },
    ];

    const trainingOptions = [
        { label: "Ophthalmologist", value: '1' },
        { label: "Resident / trainee", value: '2' },
        { label: "Cataract surgeon", value: '3' },
    ];

    const typeOptions = [
        { label: "ICCE", value: '1' },
        { label: "ECCE", value: '2' },
        { label: "SICS", value: '3' },
        { label: "Phaco", value: '4' },
    ];

    const iolOptions = [
        { label: "PC-IOL", value: '1' },
        { label: "AC-IOL", value: '2' },
        { label: "No IOL", value: '3' },
        { label: "Iris Claw IOL", value: '4' },
        { label: "Scleral Fixed IOL", value: '5' },
    ];

    const complicationOptions = [
        { label: "None", value: '1' },
        { label: "Capsule rupture without vitreous loss", value: '2' },
        { label: "Vitreous loss", value: '3' },
        { label: "Zonular dehiscence", value: '4' },
        { label: "Retained lens matter", value: '5' },
        { label: "Wound leak", value: '6' },
        { label: "Striate keratopathy", value: '7' },
        { label: "Endophthalmitis", value: '8' },
        { label: "Others", value: '9' },
    ];
    
    const incisionOptions = [
        { label: "corneal", value: 'corneal' },
        { label: "limbal", value: 'limbal' },
        { label: "tunnel", value: 'tunnel' },
    ];

    const capsulotomyOptions = [
        { label: "linear", value: 'linear' },
        { label: "can opener", value: 'can opener' },
        { label: "other", value: 'other' },
    ];

    const sutureOptions = [
        { label: "no suture", value: 'no suture' },
        { label: "continuous", value: 'continuous' },
        { label: "interrupted", value: 'interrupted' },
    ];


    // --- Helper Components ---

    const VAGrid = ({ title, name, valueRight, valueLeft, onChange }) => (
        <div className="va-row">
            <span className="va-label">{title}: VA</span>
            <div className="va-input-group">
                <input
                    type="text"
                    name={`${name}Right`}
                    value={valueRight}
                    onChange={onChange}
                    className="va-input"
                    aria-label={`${title} Visual Acuity Right Eye`}
                />
                <input
                    type="text"
                    name={`${name}Left`}
                    value={valueLeft}
                    onChange={onChange}
                    className="va-input"
                    aria-label={`${title} Visual Acuity Left Eye`}
                />
            </div>
        </div>
    );
    
    const LensRadioRow = ({ label, value, formData, onChange }) => (
        <div className="radio-row">
            <span className="radio-label">{label}</span>
            <div className="radio-eye-group">
                <input
                    type="radio"
                    id={`lens-right-${value}`}
                    name="lensRight"
                    value={String(value)}
                    checked={formData.lensRight === String(value)}
                    onChange={onChange}
                />
                <label htmlFor={`lens-right-${value}`}>O ({value})</label>
            </div>
            <div className="radio-eye-group">
                <input
                    type="radio"
                    id={`lens-left-${value}`}
                    name="lensLeft"
                    value={String(value)}
                    checked={formData.lensLeft === String(value)}
                    onChange={onChange}
                />
                <label htmlFor={`lens-left-${value}`}>O ({value})</label>
            </div>
        </div>
    );

    const PathologyRadioRow = ({ label, value, formData, onChange }) => (
        <div className="pathology-row">
            <input
                type="radio"
                id={`pathology-${value}`}
                name="pathology"
                value={String(value)}
                checked={formData.pathology === String(value)}
                onChange={onChange}
            />
            <label htmlFor={`pathology-${value}`}>O ({value})</label>
            <span className="pathology-label">{label}</span>
        </div>
    );


    // --- CSS Styles ---

    const styles = `
        /* General Reset and Container */
        .form-container {
            font-family: Arial, sans-serif;
            max-width: 800px;
            margin: 20px auto;
            border: 1px solid #000;
            padding: 0;
            display: flex;
            flex-direction: column;
            box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
            background-color: #fff;
        }
        
        h1, h2, h3 {
            margin: 0;
            padding: 0;
            font-weight: bold;
        }

        /* Top Grid Layout (B. PRE-OP) */
        .top-grid {
            display: grid;
            grid-template-columns: 2fr 1fr; /* Main Content | Visual Acuity Chart */
            border-bottom: 1px solid #000;
        }
        
        .section-b-main, .section-c-main {
            padding: 15px;
        }

        /* B. PRE-OP EXAMINATION */
        .va-header {
            display: flex;
            justify-content: flex-end;
            margin-bottom: 5px;
            font-size: 0.9em;
            font-weight: bold;
        }
        
        .va-header-group {
            display: grid;
            grid-template-columns: repeat(2, 60px);
            gap: 1px;
            margin-left: 20px;
        }

        .va-row {
            display: flex;
            align-items: center;
            margin-bottom: 5px;
        }
        
        .va-label {
            font-weight: bold;
            margin-right: 10px;
            width: 150px;
            font-size: 0.9em;
        }
        
        .va-input-group {
            display: grid;
            grid-template-columns: 60px 60px;
            gap: 1px;
            border: 1px solid #000;
        }
        
        .va-input {
            border: none;
            padding: 3px;
            text-align: center;
            height: 20px;
        }

        /* Lens Examination */
        .lens-exam-section {
            margin-top: 15px;
        }

        .lens-header {
            display: flex;
            justify-content: flex-end;
            padding-right: 30px;
            font-weight: bold;
            font-size: 0.9em;
        }

        .radio-row {
            display: grid;
            grid-template-columns: 1fr 60px 60px; /* Label | Right Eye Radio | Left Eye Radio */
            align-items: center;
            gap: 10px;
            font-size: 0.9em;
        }
        
        .radio-label {
            padding-left: 10px;
        }
        
        .radio-eye-group {
            display: flex;
            align-items: center;
            justify-content: flex-end;
        }
        
        .radio-eye-group label {
            margin-left: 5px;
        }
        
        /* Pathology */
        .pathology-section {
            margin-top: 20px;
        }

        .pathology-row {
            display: flex;
            align-items: center;
            margin-left: 10px;
            margin-bottom: 3px;
            font-size: 0.9em;
        }

        .pathology-row input[type="radio"] {
            margin-right: 5px;
        }
        
        .pathology-row label {
            margin-right: 15px;
        }
        
        .pathology-label {
            flex-grow: 1;
        }

        /* Other & Specify Row */
        .pathology-other-row {
            display: flex;
            align-items: center;
            margin-left: 10px;
            font-size: 0.9em;
        }

        .pathology-other-row input[type="text"] {
            border: none;
            border-bottom: 1px solid #000;
            flex-grow: 1;
            margin-left: 10px;
            padding: 2px;
        }
        
        /* Optional B */
        .optional-section {
            margin-top: 20px;
            border-top: 1px solid #000;
            padding-top: 15px;
        }

        .optional-b-row {
            display: flex;
            align-items: center;
            margin-bottom: 10px;
            font-size: 0.9em;
        }
        
        .optional-b-row > span, .optional-b-row > label {
            font-weight: bold;
            margin-right: 5px;
        }
        
        .optional-b-row input[type="text"] {
            border: none;
            border-bottom: 1px solid #000;
            padding: 2px;
            width: 50px;
            margin-right: 15px;
        }
        
        /* C. SURGERY Section */
        .surgery-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            border-bottom: 1px solid #000;
        }
        
        .surgery-col-left, .surgery-col-right {
            padding: 15px;
        }
        
        .surgery-col-left {
            border-right: 1px solid #000;
        }

        .input-line {
            display: flex;
            align-items: center;
            margin-bottom: 10px;
            font-size: 0.9em;
        }
        
        .input-line label {
            font-weight: bold;
            margin-right: 5px;
            white-space: nowrap;
        }

        .input-line input[type="text"] {
            border: none;
            border-bottom: 1px solid #000;
            padding: 2px;
            flex-grow: 1;
        }
        
        .date-input {
            width: 80px;
            text-align: center;
        }

        /* Radio Group Styling (C. SURGERY) */
        .radio-group-container {
            margin-bottom: 15px;
        }
        
        .radio-group-header {
            font-weight: bold;
            margin-bottom: 5px;
            font-size: 0.95em;
        }

        .radio-option-row {
            display: flex;
            align-items: center;
            margin-bottom: 3px;
            font-size: 0.85em;
        }
        
        .radio-option-row label {
            margin-left: 5px;
            width: 150px;
        }
        
        .iol-surgery-group {
            display: flex;
            justify-content: space-between;
            margin-top: 15px;
        }

        /* Complications (Checkbox Group) */
        .complication-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 10px;
            margin-top: 10px;
        }
        
        .complication-grid label {
            display: flex;
            align-items: center;
            font-size: 0.85em;
        }

        .complication-grid input[type="checkbox"] {
            margin-right: 5px;
        }

        /* Optional C */
        .optional-c-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            padding: 15px;
        }
        
        .optional-c-section {
            display: flex;
            flex-direction: column;
            font-size: 0.9em;
        }

        .optional-c-section .radio-option-row {
            margin-bottom: 0px;
        }

        .optional-c-section input[type="text"] {
            border: none;
            border-bottom: 1px solid #000;
            padding: 2px;
            width: 100px;
            margin-left: 5px;
        }

        /* Footer for State */
        .form-state-output {
            padding: 15px;
            background-color: #f4f4f4;
            border-top: 1px solid #000;
            font-size: 0.75em;
        }
        
        /* Media Query for Mobile/Small Screens */
        @media (max-width: 767px) {
            .top-grid, .surgery-grid {
                grid-template-columns: 1fr;
            }
            .surgery-col-left {
                border-right: none;
                border-bottom: 1px solid #000;
            }
            .optional-c-grid {
                grid-template-columns: 1fr;
                gap: 10px;
            }
            .va-header-group {
                margin-left: 5px;
            }
            .va-label {
                width: 120px;
            }
        }
    `;

    return (
        <div className="form-container">
            <style>{styles}</style>
            
            <div className="top-grid">
                
                {/* --- Left Column: B. PRE-OPERATIVE EXAMINATION (MAIN) --- */}
                <div className="section-b-main">
                    <h1 style={{fontSize: '1.2em', borderBottom: '2px solid #000', paddingBottom: '5px', marginBottom: '10px'}}>B. PRE - OPERATIVE EXAMINATION</h1>
                    
                    {/* Visual Acuity Section */}
                    <div className="va-section">
                        <h2 style={{fontSize: '1em', marginBottom: '10px'}}>Visual Acuity:</h2>
                        
                        <div className="va-header">
                            <span>Right eye</span>
                            <div className="va-header-group" style={{marginLeft: '10px'}}>
                                <span>VA</span>
                                <span>VA</span>
                            </div>
                        </div>

                        <VAGrid 
                            title="Presenting" 
                            name="vaPresenting" 
                            valueRight={formData.vaPresentingRight} 
                            valueLeft={formData.vaPresentingLeft} 
                            onChange={handleChange}
                        />
                        <VAGrid 
                            title="Best' or pinhole" 
                            name="vaBest" 
                            valueRight={formData.vaBestRight} 
                            valueLeft={formData.vaBestLeft} 
                            onChange={handleChange}
                        />
                    </div>

                    {/* Lens Examination Section */}
                    <div className="lens-exam-section">
                        <h2 style={{fontSize: '1em', marginBottom: '5px'}}>Lens Examination:</h2>
                        <div className="lens-header">
                            <span style={{width: '60px', marginRight: '10px'}}>Right</span>
                            <span style={{width: '60px'}}>Left</span>
                        </div>

                        {lensExamOptions.map(option => (
                            <LensRadioRow key={option.value} label={option.label} value={option.value} formData={formData} onChange={handleChange} />
                        ))}
                    </div>

                    {/* Other Ocular Pathology Section */}
                    <div className="pathology-section">
                        <h2 style={{fontSize: '1em', marginBottom: '10px'}}>Other ocular pathology in the eye to be operated, likely to affect outcome:</h2>
                        
                        {pathologyOptions.map(option => (
                            <PathologyRadioRow key={option.value} label={option.label} value={option.value} formData={formData} onChange={handleChange} />
                        ))}

                        {/* Other & specify */}
                        <div className="pathology-other-row">
                            <input
                                type="radio"
                                id="pathology-5"
                                name="pathology"
                                value="5"
                                checked={formData.pathology === "5"}
                                onChange={handleChange}
                            />
                            <label htmlFor="pathology-5">O (5)</label>
                            <span>Other & specify:</span>
                            <input
                                type="text"
                                name="pathologySpecify"
                                value={formData.pathologySpecify}
                                onChange={handleChange}
                                disabled={formData.pathology !== "5"}
                            />
                        </div>
                    </div>
                </div>

                {/* --- Right Column: Visual Acuity Chart / Clinical Data Placeholder --- */}
                <div style={{padding: '15px', borderLeft: '1px solid #000', fontSize: '0.8em', borderBottom: '1px solid #000'}}>
                    <h3 style={{marginBottom: '10px'}}>Category of Visual Acuity (Snellen)</h3>
                    {/* Placeholder content for the chart */}
                    <pre style={{border: '1px solid #ccc', padding: '10px', backgroundColor: '#f9f9f9', whiteSpace: 'pre-wrap'}}>
                        1. 6/6 | 9. 1/60
                        2. 6/9 | 10. PL+
                        3. 6/12 | 11. NPL
                        4. 6/18 | 12. Cannot examine, believed &gt;6/60
                        5. 6/24 |
                        6. 6/36 | 13. Cannot examine, believed &lt;6/60
                        7. 6/60 |
                        8. 3/60
                    </pre>
                    <h3 style={{marginTop: '20px', textAlign: 'center', border: '1px solid #000', padding: '5px', backgroundColor: '#ddd'}}>CLINICAL DATA</h3>
                    {/* Placeholder for any clinical data inputs/summary */}
                </div>

            </div>
            
            {/* --- Optional B Section --- */}
            <div className="optional-section">
                <h3 style={{fontSize: '1em', marginBottom: '10px', paddingLeft: '15px'}}>Optional:</h3>
                
                <div className="optional-b-row" style={{paddingLeft: '15px'}}>
                    <label>Eye to be operated:</label>
                    <input type="radio" id="eye-right" name="eyeToOperate" value="Right" checked={formData.eyeToOperate === 'Right'} onChange={handleChange} />
                    <label htmlFor="eye-right">Right: O (1)</label>
                    <input type="radio" id="eye-left" name="eyeToOperate" value="Left" checked={formData.eyeToOperate === 'Left'} onChange={handleChange} />
                    <label htmlFor="eye-left">Left: O (2)</label>
                </div>
                
                <div className="optional-b-row" style={{paddingLeft: '15px'}}>
                    <label>Refraction:</label>
                    <span>sp</span>
                    <input type="text" name="refractionSphere" value={formData.refractionSphere} onChange={handleChange} />
                    <span>cyc</span>
                    <input type="text" name="refractionCylinder" value={formData.refractionCylinder} onChange={handleChange} />
                    <span>axis</span>
                    <input type="text" name="refractionAxis" value={formData.refractionAxis} onChange={handleChange} />
                </div>
                
                <div className="optional-b-row" style={{paddingLeft: '15px'}}>
                    <label>Targeted post-op. spherical equivalent:</label>
                    <span>sp</span>
                    <input type="text" name="targetedSphericalEquivalent" value={formData.targetedSphericalEquivalent} onChange={handleChange} style={{width: '70px'}} />
                </div>
                
                <div className="optional-b-row" style={{paddingLeft: '15px'}}>
                    <label>Biometry:</label>
                    <span>K1:</span>
                    <input type="text" name="biometryK1" value={formData.biometryK1} onChange={handleChange} />
                    <span>K2:</span>
                    <input type="text" name="biometryK2" value={formData.biometryK2} onChange={handleChange} />
                    <span>Axial length:</span>
                    <input type="text" name="axialLength" value={formData.axialLength} onChange={handleChange} style={{width: '70px'}} />
                </div>
            </div>

            {/* --- C. SURGERY SECTION --- */}
            <div className="surgery-grid">
                
                {/* --- C: SURGERY (LEFT HALF) --- */}
                <div className="surgery-col-left">
                    <h1 style={{fontSize: '1.2em', borderBottom: '2px solid #000', paddingBottom: '5px', marginBottom: '10px'}}>C: SURGERY</h1>
                    
                    {/* Date of Operation */}
                    <div className="input-line">
                        <label htmlFor="date" style={{width: '140px'}}>Date of operation:</label>
                        <input
                            type="text"
                            id="date"
                            name="date"
                            value={formData.date}
                            onChange={handleChange}
                            className="date-input"
                            placeholder="/ /"
                        />
                    </div>
                    
                    {/* Place of Operation */}
                    <div className="radio-group-container">
                        <span className="radio-group-header">Place of operation:</span>
                        {placeOptions.map(option => (
                            <div className="radio-option-row" key={option.value}>
                                <input type="radio" id={`place-${option.value}`} name="placeOfOperation" value={option.value} checked={formData.placeOfOperation === option.value} onChange={handleChange} />
                                <label htmlFor={`place-${option.value}`}>{option.label}</label>
                                <span>O ({option.value})</span>
                            </div>
                        ))}
                    </div>

                    {/* Type of Surgery and IOL */}
                    <div className="iol-surgery-group">
                        <div style={{width: '45%'}}>
                            <span className="radio-group-header">Type of surgery:</span>
                            {typeOptions.map(option => (
                                <div className="radio-option-row" key={option.value}>
                                    <input type="radio" id={`type-${option.value}`} name="typeOfSurgery" value={option.value} checked={formData.typeOfSurgery === option.value} onChange={handleChange} />
                                    <label htmlFor={`type-${option.value}`}>{option.label}</label>
                                    <span>O ({option.value})</span>
                                </div>
                            ))}
                        </div>

                        <div style={{width: '45%'}}>
                            <span className="radio-group-header">IOL:</span>
                            {iolOptions.map(option => (
                                <div className="radio-option-row" key={option.value}>
                                    <input type="radio" id={`iol-${option.value}`} name="iol" value={option.value} checked={formData.iol === option.value} onChange={handleChange} />
                                    <label htmlFor={`iol-${option.value}`}>{option.label}</label>
                                    <span>O ({option.value})</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* --- C: SURGERY (RIGHT HALF) --- */}
                <div className="surgery-col-right">
                    
                    {/* Hospital / camp ID and Surgeon ID */}
                    <div className="input-line">
                        <label htmlFor="hospitalId">Hospital / camp ID:</label>
                        <input type="text" id="hospitalId" name="hospitalId" value={formData.hospitalId} onChange={handleChange} />
                    </div>
                    <div className="input-line">
                        <label htmlFor="surgeonId">Surgeon ID:</label>
                        <input type="text" id="surgeonId" name="surgeonId" value={formData.surgeonId} onChange={handleChange} />
                    </div>

                    {/* Training */}
                    <div className="radio-group-container">
                        <span className="radio-group-header">Training</span>
                        {trainingOptions.map(option => (
                            <div className="radio-option-row" key={option.value}>
                                <input type="radio" id={`training-${option.value}`} name="training" value={option.value} checked={formData.training === option.value} onChange={handleChange} />
                                <label htmlFor={`training-${option.value}`}>{option.label}</label>
                                <span>O ({option.value})</span>
                            </div>
                        ))}
                    </div>

                    {/* Operative Complications */}
                    <div className="radio-group-container">
                        <span className="radio-group-header">Operative complications in operated eye:</span>
                        <div className="complication-grid">
                            {/* Left Column (1-5) */}
                            <div className="flex flex-col">
                                {complicationOptions.slice(0, 5).map(option => (
                                    <label key={option.value}>
                                        <input type="checkbox" name="complication" value={option.value} checked={formData.complication.includes(option.value)} onChange={handleComplicationChange} />
                                        <span>O ({option.value})</span>
                                        <span style={{marginLeft: '5px'}}>{option.label}</span>
                                    </label>
                                ))}
                            </div>
                            {/* Right Column (6-9) */}
                            <div className="flex flex-col">
                                {complicationOptions.slice(5).map(option => (
                                    <div key={option.value}>
                                        <label>
                                            <input type="checkbox" name="complication" value={option.value} checked={formData.complication.includes(option.value)} onChange={handleComplicationChange} />
                                            <span>O ({option.value})</span>
                                            <span style={{marginLeft: '5px'}}>{option.label}</span>
                                        </label>
                                        {/* Specify input for "Others (9)" */}
                                        {option.value === '9' && formData.complication.includes('9') && (
                                            <input
                                                type="text"
                                                name="otherComplicationSpecify"
                                                value={formData.otherComplicationSpecify}
                                                onChange={handleChange}
                                                placeholder="Specify here..."
                                                style={{border: 'none', borderBottom: '1px solid #000', marginTop: '5px', padding: '2px', width: '90%'}}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- Optional C Section --- */}
            <div className="optional-section">
                <h3 style={{fontSize: '1em', marginBottom: '10px', paddingLeft: '15px'}}>Optional:</h3>
                
                <div className="optional-c-grid">
                    
                    {/* Incision */}
                    <div className="optional-c-section">
                        <span className="radio-group-header">Incision:</span>
                        {incisionOptions.map((option, index) => (
                            <div className="radio-option-row" key={option.value}>
                                <input type="radio" id={`incision-${option.value}`} name="incisionType" value={option.value} checked={formData.incisionType === option.value} onChange={handleChange} />
                                <label htmlFor={`incision-${option.value}`}>{option.label}</label>
                                <span>O ({index + 1})</span>
                            </div>
                        ))}
                    </div>

                    {/* Capsulotomy */}
                    <div className="optional-c-section">
                        <span className="radio-group-header">Capsulotomy:</span>
                        {capsulotomyOptions.map((option, index) => (
                            <div className="radio-option-row" key={option.value}>
                                <input type="radio" id={`capsulotomy-${option.value}`} name="capsulotomyType" value={option.value} checked={formData.capsulotomyType === option.value} onChange={handleChange} />
                                <label htmlFor={`capsulotomy-${option.value}`}>{option.label}</label>
                                <span>O ({index + 1})</span>
                            </div>
                        ))}
                        <div style={{marginTop: '10px'}} className="input-line">
                            <label style={{fontWeight: 'normal'}}>Type IOL:</label>
                            <input type="text" name="typeIOL" value={formData.typeIOL} onChange={handleChange} style={{flexGrow: 0, width: '100px'}}/>
                        </div>
                    </div>

                    {/* Suture */}
                    <div className="optional-c-section">
                        <span className="radio-group-header">Suture:</span>
                        {sutureOptions.map((option, index) => (
                            <div className="radio-option-row" key={option.value}>
                                <input type="radio" id={`suture-${option.value}`} name="sutureType" value={option.value} checked={formData.sutureType === option.value} onChange={handleChange} />
                                <label htmlFor={`suture-${option.value}`}>{option.label}</label>
                                <span>O ({index + 1})</span>
                            </div>
                        ))}
                        <div style={{marginTop: '10px'}} className="input-line">
                            <label style={{fontWeight: 'normal'}}>No. of sutures:</label>
                            <input type="text" name="numberOfSutures" value={formData.numberOfSutures} onChange={handleChange} style={{flexGrow: 0, width: '40px'}}/>
                        </div>
                    </div>
                    
                    {/* IOL Power */}
                    <div className="optional-c-section">
                        <div style={{marginTop: '10px'}} className="input-line">
                            <label style={{fontWeight: 'normal'}}>IOL power:</label>
                            <input type="text" name="iolPower" value={formData.iolPower} onChange={handleChange} style={{flexGrow: 0, width: '100px'}}/>
                        </div>
                    </div>

                </div>
            </div>


            {/* Debugging Output */}
            {/* <div className="form-state-output">
                <h3>Current Form State:</h3>
                <pre>{JSON.stringify(formData, null, 2)}</pre>
            </div> */}
        </div>
    );
};

export default OpthalmologyyExamination;